import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useNavigate, useSearchParams } from 'react-router';

import logo from '../../assets/images/logo-sami-with-name.svg';
import { PageTitle } from '@/components/nav/PageTitle';
import { Loader } from '@/components/generic/Loader';
import { useTranslation } from '@/hooks/useTranslation';
import { MessageBox, MessageBoxType } from '@/components/layout/MessageBox';
import symfonyClient, { getSymfonyApiUrl } from '@/services/SymfonyClient';
import { useFirebaseAuth } from '@/providers/FirebaseProvider';
import { signInWithCustomToken } from 'firebase/auth';
import logoCarbonHub from '../../assets/images/logo.svg';

type SymfonyLoginResponse = {
  authenticated: boolean;
  userIdentifier: string;
  firebaseCustomToken?: string;
};

export function LoginSymfonyScreen() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const firebaseAuth = useFirebaseAuth();
  const hasExchangedOAuthCodeRef = useRef(false);

  const destination = useMemo(() => {
    const askedDestination = searchParams.get('destination');

    if (
      askedDestination &&
      askedDestination.startsWith('/') &&
      !askedDestination.startsWith('//')
    ) {
      return askedDestination;
    }

    return '/';
  }, [searchParams]);

  useEffect(() => {
    const oauthError = searchParams.get('oauth_error');
    if (oauthError) {
      setError(t('nav:login.wrong_credentials'));
    }
  }, [searchParams, t]);

  useEffect(() => {
    const oauthCode = searchParams.get('oauth_code');
    if (!oauthCode || hasExchangedOAuthCodeRef.current) {
      return;
    }

    hasExchangedOAuthCodeRef.current = true;
    setIsLoading(true);
    setError(null);

    void signInWithFirebaseToken('/auth/exchange', { code: oauthCode });
  }, [firebaseAuth, navigate, searchParams, t]);

  const startCarbonHubOAuth = () => {
    setIsLoading(true);
    setError(null);

    const oauthStartUrl = `${getSymfonyApiUrl()}/auth/carbon-hub/start?destination=${encodeURIComponent(destination)}`;
    window.location.assign(oauthStartUrl);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setIsLoading(true);
    setError(null);

    void signInWithFirebaseToken('/auth/login', { email, password });
  };

  const signInWithFirebaseToken = async (postUrl: string, data: any) => {
    try {
      const response = await symfonyClient.post<SymfonyLoginResponse>(
        postUrl,
        data,
      );

      const firebaseCustomToken = response.data.firebaseCustomToken;
      if (!firebaseCustomToken) {
        setError(t('nav:login.wrong_credentials'));
        return;
      }

      await signInWithCustomToken(firebaseAuth, firebaseCustomToken);
      navigate(destination, { replace: true });
    } catch (_err) {
      setError(t('nav:login.wrong_credentials'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <PageTitle title={t('nav:login.signin')} />

      <div className="w-full h-screen overflow-scroll flex flex-col p-8 items-stretch">
        <div>
          <img className="h-12 w-auto" src={logo} alt="Sami logo" />
        </div>
        <div className="grow flex gap-8 justify-between items-center self-center max-w-xl">
          <div>
            <h1>{t('nav:login.welcome') + ' - symfony'}</h1>
            <div className="mt-8">
              <form
                name="loginSymfonyForm"
                className="space-y-4"
                onSubmit={handleSubmit}
              >
                <div className="space-y-1">
                  <label htmlFor="email">{t('nav:login.email')}</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setError(null);
                    }}
                    required
                  />
                </div>

                <div className="space-y-1">
                  <label htmlFor="password">{t('nav:login.password')}</label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setError(null);
                    }}
                    required
                  />
                </div>

                <div className="flex justify-center">
                  {isLoading ? (
                    <Loader className="w-10 h-10 text-black" />
                  ) : (
                    <input
                      className="primary purple w-full"
                      type="submit"
                      value={t('nav:login.signin')}
                    />
                  )}
                </div>

                <div className="flex justify-center">
                  <NavLink
                    to="/forgot-password"
                    className="text-gray-500 italic text-sm"
                  >
                    {t('nav:login.password_forgotten')}
                  </NavLink>
                </div>

                <hr className="border border-gray flex" />
                <div className="flex justify-center">
                  <button
                    type="button"
                    className="secondary gray"
                    onClick={startCarbonHubOAuth}
                    disabled={isLoading}
                  >
                    <img
                      className="h-6 w-auto"
                      src={logoCarbonHub}
                      alt="Carbon Hub logo"
                    />
                    <span className="background-[#1b998b] green">
                      {t('nav:login.login_with_carbon_hub')}
                    </span>
                  </button>
                </div>

                {error && (
                  <MessageBox type={MessageBoxType.Error}>
                    <p>{error}</p>
                  </MessageBox>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
